/**
 * FolderSelectDialog — Dialog for selecting a library folder when saving articles
 * 
 * Features:
 * - Shows digest-based folders (auto-created from digest profiles)
 * - Shows custom user folders with subfolders (max 3 levels)
 * - Shows "Ungrouped" as default option
 * - Allows creating new folders inline
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import {
  Folder,
  FolderPlus,
  ChevronRight,
  Check,
  Loader2,
  Stethoscope,
  Archive,
} from 'lucide-react';
import { libraryAPI } from '../../api';

interface FolderItem {
  folder: string;
  type: string;  // 'digest' | 'custom' | 'system'
  count: number;
  subfolders?: FolderItem[];
  profile_id?: string;
  specialty_id?: string;
  folder_id?: string;
}

interface FolderSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (folder: string) => void;
  articleTitle?: string;
}

export const FolderSelectDialog: React.FC<FolderSelectDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
  articleTitle,
}) => {
  const [folders, setFolders] = React.useState<FolderItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedFolder, setSelectedFolder] = React.useState<string>('Ungrouped');
  const [showNewFolder, setShowNewFolder] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await libraryAPI.getFolders();
      setFolders((response.folders || []) as FolderItem[]);
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      setCreating(true);
      await libraryAPI.createFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolder(false);
      await loadFolders();
    } catch (error) {
      console.error('Failed to create folder:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleConfirm = () => {
    onSelect(selectedFolder);
    onOpenChange(false);
  };

  const renderFolderIcon = (type: string) => {
    switch (type) {
      case 'digest':
        return <Stethoscope className="w-4 h-4 text-[hsl(var(--primary))]" />;
      case 'system':
        return <Archive className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />;
      default:
        return <Folder className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />;
    }
  };

  const renderFolderItem = (folder: FolderItem, depth: number = 0) => (
    <React.Fragment key={folder.folder}>
      <button
        onClick={() => setSelectedFolder(folder.folder)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
          selectedFolder === folder.folder
            ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
            : 'hover:bg-[hsl(var(--muted))]'
        }`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        data-testid={`folder-option-${folder.folder.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {renderFolderIcon(folder.type)}
        <span className="flex-1 truncate">{folder.folder}</span>
        <Badge variant="secondary" className="text-xs">
          {folder.count}
        </Badge>
        {selectedFolder === folder.folder && (
          <Check className="w-4 h-4" />
        )}
      </button>
      
      {folder.subfolders && folder.subfolders.length > 0 && (
        <div className="ml-4">
          {folder.subfolders.map((sub) => renderFolderItem(sub, depth + 1))}
        </div>
      )}
    </React.Fragment>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="folder-select-dialog">
        <DialogHeader>
          <DialogTitle>Save to LitHub</DialogTitle>
          <DialogDescription>
            {articleTitle ? (
              <>Choose a folder for "{articleTitle.slice(0, 50)}..."</>
            ) : (
              <>Select a folder to organize this article</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" />
            </div>
          ) : (
            <ScrollArea className="h-[280px] pr-4">
              <div className="space-y-1">
                {/* Digest-based folders first */}
                {folders.filter(f => f.type === 'digest').length > 0 && (
                  <>
                    <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-3 py-1.5 uppercase tracking-wide">
                      Digest Folders
                    </p>
                    {folders.filter(f => f.type === 'digest').map((folder) => renderFolderItem(folder))}
                    <Separator className="my-2" />
                  </>
                )}

                {/* Custom folders */}
                {folders.filter(f => f.type === 'custom').length > 0 && (
                  <>
                    <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-3 py-1.5 uppercase tracking-wide">
                      Custom Folders
                    </p>
                    {folders.filter(f => f.type === 'custom').map((folder) => renderFolderItem(folder))}
                    <Separator className="my-2" />
                  </>
                )}

                {/* System folders (Ungrouped) */}
                <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-3 py-1.5 uppercase tracking-wide">
                  Default
                </p>
                <button
                  onClick={() => setSelectedFolder('Ungrouped')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    selectedFolder === 'Ungrouped'
                      ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                      : 'hover:bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="folder-option-ungrouped"
                >
                  <Archive className="w-4 h-4" />
                  <span className="flex-1">Ungrouped</span>
                  {selectedFolder === 'Ungrouped' && (
                    <Check className="w-4 h-4" />
                  )}
                </button>

                {/* Ungrouped from API if present */}
                {folders.filter(f => f.type === 'system').map((folder) => renderFolderItem(folder))}
              </div>
            </ScrollArea>
          )}

          {/* Create new folder section */}
          <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
            {showNewFolder ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Folder name..."
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  autoFocus
                  data-testid="new-folder-input"
                />
                <Button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim() || creating}
                  size="sm"
                  data-testid="create-folder-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowNewFolder(false)}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowNewFolder(true)}
                className="w-full"
                data-testid="show-new-folder-btn"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Create New Folder
              </Button>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} data-testid="confirm-folder-btn">
            <Check className="w-4 h-4 mr-2" />
            Save to {selectedFolder}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FolderSelectDialog;
